import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ENABLE_INTERCOM_TOKEN, INTERCOM_APP_ID } from '../../constants';
import userManager from '../../utils/userManager';
import { useGetIndividualProfile } from '../../queries/hooks/userHooks';
import { useConsentGroups } from '../../hooks/useConsentGroups';
import { useGetIntercomToken } from '../../queries/hooks/intercomHooks';

export const IntercomManager = () => {
  const {
    profileLoading,
    linkedOrgsLoading,
    roles,
    name,
    email,
    userId,
    org,
  } = useSelector((state) => ({
    profileLoading: state.app.getIn(['currentUser', 'profileLoading']),
    linkedOrgsLoading: state.app.getIn(['currentUser', 'linkedOrgsLoading']),
    name: state.app.getIn(['currentUser', 'fullName']),
    email: state.app.getIn(['currentUser', 'emailAddress']),
    userId: state.app.getIn(['currentUser', 'id']),
    org: state.app.getIn(['currentUser', 'linkedOrgs']).toJS() || [],
    roles: state.app.getIn(['currentUser', 'linkedUserRoles']).toJS() || [],
  }));

  const { isFunctionalGroupActive } = useConsentGroups();
  const { data: intercomToken } = useGetIntercomToken();
  const {
    data: customer,
    isLoading: isCustomerLoading,
  } = useGetIndividualProfile();


  const ministryArea =
    customer?.profile?.IndividualProfile?.ministryFocuses?.[0]?.type;
  const otherLeaderDescription =
    ministryArea === 'OTHER'
      ? customer?.profile?.IndividualProfile?.ministryFocuses?.[0]
          ?.customDescription
      : '';

  const linkedOrg = org?.[0] || {};
  const orgRoles = roles?.[0]?.assignedRoles
    ?.filter(
      (role) =>
        role.contextId === linkedOrg?.organizationProfile?.organization?.id &&
        role.name !== 'Billing Account Purchaser'
    )
    ?.map((role) => role?.name?.replace('Organization ', ''))
    ?.toString();

  const hasIntercomToken = ENABLE_INTERCOM_TOKEN ? !!intercomToken?.message : true;

  useEffect(() => {
    if (isFunctionalGroupActive && hasIntercomToken) {
      window.Intercom('boot', {
        app_id: INTERCOM_APP_ID,
        ...(ENABLE_INTERCOM_TOKEN && {intercom_user_jwt: intercomToken?.message}),
      });
    } else {
      window.Intercom('shutdown');
    }
  }, [isFunctionalGroupActive, intercomToken, hasIntercomToken]);

  useEffect(() => {
    const shutdownIntercom = () => {
      window.Intercom('shutdown');
    };

    userManager.events.addUserSignedOut(shutdownIntercom);

    return () => {
      userManager.events.removeUserSignedOut(shutdownIntercom);
    };
  }, []);

  useEffect(() => {
    if (
      profileLoading === 'success' &&
      linkedOrgsLoading === 'success' &&
      !isCustomerLoading
    ) {
      const aomAccountNumber = linkedOrg?.organizationProfile?.billingAccounts?.find(
        (ba) => ba?.active
      )?.accountNumber;
      window.Intercom('update', {
        name,
        email,
        user_id: userId,
        aom_orgId: linkedOrg?.organizationProfile?.organization?.id,
        aom_orgName: linkedOrg?.organizationProfile?.organization?.name,
        aom_accountNumber: aomAccountNumber,
        aom_organizationRoles: orgRoles,
        aom_ministryArea: ministryArea,
        aom_otherLeaderText: otherLeaderDescription,
      });
    }
  }, [
    profileLoading,
    linkedOrgsLoading,
    name,
    email,
    userId,
    orgRoles,
    linkedOrg,
    isCustomerLoading,
    ministryArea,
    otherLeaderDescription,
  ]);

  return null;
};

export const shutdownIntercom = () => {
  window.Intercom('shutdown');
};

export default IntercomManager;
